import { Injectable } from '@angular/core';
import { ImpersonationPermissionRuleEntity } from 'mj_generatedentities';
import { DataFetchService } from './data-fetch.service';
import { EntityNames, SYSTEM_ROLES } from '../utils/constants';
import { SharedData } from '../utils/shared-data';
import { UserAuthService } from './user-auth.service';
import { LogStatus, LogError } from '@memberjunction/core';

/**
 * Permission rule for role-based impersonation with priority support
 */
export interface ImpersonationPermissionRule {
  id: string;
  impersonatorRoleId: string;
  impersonatorRoleName: string;
  targetRoleId: string | null;
  targetRoleName: string | null;
  isAllowed: boolean;
  isActive: boolean;
  priority: number;
  description: string | null;
}

/**
 * Result of permission validation
 */
export interface PermissionValidationResult {
  isAllowed: boolean;
  reason: string;
  matchedRule?: ImpersonationPermissionRule;
  conflictingRules?: ImpersonationPermissionRule[];
}

/**
 * Service for managing database-driven impersonation permissions
 * Provides role-based permission checking with priority-based conflict resolution
 */
@Injectable({
  providedIn: 'root'
})
export class ImpersonationPermissionService {

  constructor(
    private dataFetchService: DataFetchService,
    private sharedData: SharedData,
    private userAuthService: UserAuthService
  ) { }

  /**
   * Validates if current user can impersonate another user based on runtime roles
   * @param targetUserId - Target user's User_ID (PREFERRED when available)
   * @param targetUserEmail - Target user's email (optional, for fallback)
   * @param targetUserPersonId - Target user's PersonId (optional, for fallback)
   * @returns Promise resolving to permission validation result
   */
  public async canImpersonate(
    targetUserId?: string,
    targetUserEmail?: string,
    targetUserPersonId?: number
  ): Promise<PermissionValidationResult> {
    try {
      LogStatus(`ImpersonationPermissionService: Validating impersonation permissions`);

      // Get current user's runtime role
      const currentUserRoleId = this.sharedData.getCurrentUserRoleId();
      if (!currentUserRoleId) {
        return {
          isAllowed: false,
          reason: 'Current user role not found - impersonation disabled'
        };
      }

      // Get target user's runtime role (using new multi-path method)
      const targetUserRoleId = await this.getTargetUserRoleId(
        targetUserId,
        targetUserEmail,
        targetUserPersonId
      );

      if (!targetUserRoleId) {
        return {
          isAllowed: false,
          reason: 'Target user role not found - impersonation not allowed'
        };
      }

      // Get all active permission rules
      const rules = await this.getAllActiveRules();

      if (!rules || rules.length === 0) {
        return {
          isAllowed: false,
          reason: 'No permission rules configured - impersonation disabled'
        };
      }

      // Find applicable rules for this impersonation request
      const applicableRules = this.findApplicableRules(
        rules,
        currentUserRoleId,
        targetUserRoleId
      );

      // Apply priority-based conflict resolution
      const resolution = this.resolvePermissionConflicts(applicableRules);

      return resolution;
    } catch (error) {
      console.error('Error validating impersonation permission:', error);
      return {
        isAllowed: false,
        reason: `Permission validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Gets target user's role ID by User_ID (preferred) or PersonId
   * Checks ExtendedUsers first for performance, then falls back to GET_ROLE_API
   *
   * @param targetUserId - Target user's User_ID (from __mj.User.ID) - PREFERRED
   * @param targetUserEmail - Target user's email (optional, for fallback)
   * @param targetUserPersonId - Target user's PersonId (optional, for fallback)
   * @returns Role ID or null if not found
   */
  private async getTargetUserRoleId(
    targetUserId?: string,
    targetUserEmail?: string,
    targetUserPersonId?: number
  ): Promise<string | null> {
    try {
      let targetRoleName: string | null = null;

      // PREFERRED: Use User_ID if available (fastest - ExtendedUsers lookup)
      if (targetUserId) {
        LogStatus(`ImpersonationPermissionService: Getting role by User_ID: ${targetUserId}`);
        targetRoleName = await this.userAuthService.getRoleByUserId(
          targetUserId,
          targetUserEmail,
          targetUserPersonId?.toString()
        );
      }
      // FALLBACK: Use email if User_ID not available
      else if (targetUserEmail) {
        LogStatus(`ImpersonationPermissionService: Getting role by email: ${targetUserEmail}`);
        targetRoleName = await this.userAuthService.getRoleByEmail(
          targetUserEmail,
          targetUserPersonId?.toString()
        );
      }
      // LAST RESORT: Direct GET_ROLE_API call (legacy path)
      else if (targetUserPersonId) {
        LogStatus(`ImpersonationPermissionService: Falling back to direct GET_ROLE_API call with PersonId: ${targetUserPersonId}`);
        const auraId = this.userAuthService.getAuraUniqueId() || '';
        targetRoleName = await this.userAuthService.fetchUserRole(
          targetUserPersonId.toString(),
          auraId
        );
      }

      if (!targetRoleName) {
        LogError(`ImpersonationPermissionService: No role found for target user`);
        return null;
      }

      // Map role name to role ID using SharedData
      const roleId = this.sharedData.getRoleIdByName(targetRoleName);
      LogStatus(`ImpersonationPermissionService: Mapped role "${targetRoleName}" to ID: ${roleId}`);

      return roleId;

    } catch (error) {
      LogError('ImpersonationPermissionService: Error getting target user role', undefined, error);
      return null;
    }
  }


  /**
   * Creates a new permission rule
   * @param rule - Permission rule to create
   * @returns Promise resolving to created rule entity
   */
  public async createPermissionRule(
    impersonatorRoleId: string,
    targetRoleId: string | null,
    isAllowed: boolean,
    priority: number = 100,
    description: string | null = null
  ): Promise<ImpersonationPermissionRuleEntity | null> {
    try {
      const entity = await this.dataFetchService.createEntityInstance<ImpersonationPermissionRuleEntity>(
        EntityNames.ImpersonationPermissionRules
      );
      
      if (!entity) {
        throw new Error('Failed to create permission rule entity');
      }

      entity.ImpersonatorRoleID = impersonatorRoleId;
      entity.TargetRoleID = targetRoleId;
      entity.IsAllowed = isAllowed;
      entity.Priority = priority;
      entity.Description = description;
      entity.IsActive = true;

      const success = await entity.Save();
      if (!success) {
        throw new Error('Failed to save permission rule');
      }

      return entity;
    } catch (error) {
      console.error('Error creating permission rule:', error);
      return null;
    }
  }

  /**
   * Gets all active permission rules with role information
   * @returns Promise resolving to array of permission rules
   */
  private async getAllActiveRules(): Promise<ImpersonationPermissionRule[]> {
    try {
      // Get rules from database
      const rulesResult = await this.dataFetchService.fetchWithParams<ImpersonationPermissionRuleEntity>({
        EntityName: EntityNames.ImpersonationPermissionRules,
        ExtraFilter: 'IsActive = 1',
        OrderBy: 'Priority DESC',
        ResultType: 'simple'
      });

      if (!rulesResult.success) {
        console.error('Error fetching permission rules');
        return [];
      }

      const rules = rulesResult.data;

      // Use SharedData role cache to get role names
      return rules.map(rule => ({
        id: rule.ID!,
        impersonatorRoleId: rule.ImpersonatorRoleID,
        impersonatorRoleName: this.sharedData.getRoleNameById(rule.ImpersonatorRoleID) || 'Unknown Role',
        targetRoleId: rule.TargetRoleID,
        targetRoleName: rule.TargetRoleID ? this.sharedData.getRoleNameById(rule.TargetRoleID) || 'Unknown Role' : null,
        isAllowed: rule.IsAllowed,
        isActive: rule.IsActive,
        priority: rule.Priority,
        description: rule.Description
      }));
    } catch (error) {
      console.error('Error fetching permission rules:', error);
      return [];
    }
  }

  /**
   * Finds permission rules that apply to the impersonation request using runtime roles
   * @param rules - All available rules
   * @param impersonatorRoleId - Current user's role ID from GET ROLE API
   * @param targetRoleId - Target user's role ID from GET ROLE API
   * @returns Array of applicable rules
   */
  private findApplicableRules(
    rules: ImpersonationPermissionRule[],
    impersonatorRoleId: string,
    targetRoleId: string
  ): ImpersonationPermissionRule[] {
    return rules.filter(rule => {
      // Check if impersonator has the required role by ID
      const hasImpersonatorRole = rule.impersonatorRoleId === impersonatorRoleId;
      if (!hasImpersonatorRole) {
        return false;
      }

      // If rule has no target role restriction (null), it applies to all targets
      if (rule.targetRoleId === null) {
        return true;
      }

      // Check if target user has the specified role by ID
      return rule.targetRoleId === targetRoleId;
    });
  }

  /**
   * Resolves permission conflicts using priority-based system
   * @param applicableRules - Rules that apply to this impersonation request
   * @returns Permission validation result with conflict resolution
   */
  private resolvePermissionConflicts(
    applicableRules: ImpersonationPermissionRule[]
  ): PermissionValidationResult {
    if (applicableRules.length === 0) {
      return {
        isAllowed: false,
        reason: 'No applicable permission rules found'
      };
    }

    // Sort by priority (highest first), then by specificity (specific roles before null roles)
    const sortedRules = [...applicableRules].sort((a, b) => {
      // First sort by priority (higher wins)
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      
      // Then sort by specificity (specific target role beats wildcard)
      const aSpecific = a.targetRoleId !== null ? 1 : 0;
      const bSpecific = b.targetRoleId !== null ? 1 : 0;
      return bSpecific - aSpecific;
    });

    const highestPriorityRule = sortedRules[0];
    const conflictingRules = sortedRules.filter(rule => 
      rule.priority === highestPriorityRule.priority && 
      rule.isAllowed !== highestPriorityRule.isAllowed
    );

    if (conflictingRules.length > 0) {
      // We have conflicting rules at same priority - more specific wins
      const specificRule = sortedRules.find(rule => 
        rule.priority === highestPriorityRule.priority && 
        rule.targetRoleId !== null
      );
      
      if (specificRule) {
        return {
          isAllowed: specificRule.isAllowed,
          reason: `Permission resolved by specific rule (Priority: ${specificRule.priority}): ${specificRule.description || `${specificRule.impersonatorRoleName} → ${specificRule.targetRoleName}`}`,
          matchedRule: specificRule,
          conflictingRules: conflictingRules
        };
      }
    }

    // No conflicts or conflicts resolved by priority
    return {
      isAllowed: highestPriorityRule.isAllowed,
      reason: highestPriorityRule.isAllowed 
        ? `Permission granted by rule (Priority: ${highestPriorityRule.priority}): ${highestPriorityRule.description || `${highestPriorityRule.impersonatorRoleName} → ${highestPriorityRule.targetRoleName || 'Any Role'}`}`
        : `Permission denied by rule (Priority: ${highestPriorityRule.priority}): ${highestPriorityRule.description || `${highestPriorityRule.impersonatorRoleName} → ${highestPriorityRule.targetRoleName || 'Any Role'}`}`,
      matchedRule: highestPriorityRule,
      conflictingRules: conflictingRules.length > 0 ? conflictingRules : undefined
    };
  }

  /**
   * Updates the IsActive status of a permission rule
   * @param ruleId - ID of the rule to update
   * @param isActive - New active status
   * @returns Promise resolving to success status
   */
  public async updateRuleActiveStatus(ruleId: string, isActive: boolean): Promise<boolean> {
    try {
      const entity = await this.dataFetchService.fetchRecordById<ImpersonationPermissionRuleEntity>(
        EntityNames.ImpersonationPermissionRules,
        ruleId
      );

      if (!entity) {
        return false;
      }

      entity.IsActive = isActive;
      return await entity.Save();
    } catch (error) {
      console.error('Error updating rule active status:', error);
      return false;
    }
  }

  /**
   * Gets permission rules for management UI
   * @returns Promise resolving to all permission rules with role names
   */
  public async getAllRulesForManagement(): Promise<ImpersonationPermissionRule[]> {
    try {
      // Get all rules from database
      const rulesResult = await this.dataFetchService.fetchMultipleEntities([
        {
          EntityName: EntityNames.ImpersonationPermissionRules,
          OrderBy: 'IsActive DESC, Priority DESC',
          ResultType: 'entity_object'
        }
      ]);

      if (!rulesResult[0].success) {
        console.error('Error fetching permission rules for management');
        return [];
      }

      const rules = rulesResult[0].data as ImpersonationPermissionRuleEntity[];

      // Use SharedData role cache to get role names
      return rules.map(rule => ({
        id: rule.ID!,
        impersonatorRoleId: rule.ImpersonatorRoleID,
        impersonatorRoleName: this.sharedData.getRoleNameById(rule.ImpersonatorRoleID) || 'Unknown Role',
        targetRoleId: rule.TargetRoleID,
        targetRoleName: rule.TargetRoleID ? this.sharedData.getRoleNameById(rule.TargetRoleID) || 'Unknown Role' : null,
        isAllowed: rule.IsAllowed,
        isActive: rule.IsActive,
        priority: rule.Priority,
        description: rule.Description
      }));
    } catch (error) {
      console.error('Error fetching rules for management:', error);
      return [];
    }
  }
}